import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CardHeader, Col, Row } from "reactstrap";
import ResourceSelect from "../../components/Inputs/ResourceSelect";
import ColumnToggle from "../../components/NTable/Actions/ColumnToggle";
import Filters from "../../components/NTable/Actions/Filters";
import Select from "../../components/Inputs/Select";
import {
	getSettingsResourceSearchFilters,
	renderSettingsResourceLabel,
	renderCompanyResourceLabel,
} from "../../utils/helpers/settings";
import { getCompaniesSearchFilters } from "../Profile/Personal/Actions/ChangeCompanyInformation";
import { useColumnFiltersConfig } from "../../components/NTable/hook/useColumnFiltersConfig";
import { useCustomFields } from "../Settings/CustomFields/context";

export const manageAbilityOptions = [
  "direct-users",
  "manage-users",
  "direct-users-and-organisation-level",
  "direct-org-level-and-users",
  "direct-users-and-location",
  "direct-users-and-organisation-level-and-location",
  "direct-company",
  "direct-users-and-pay-groups",
  "direct-users-and-calculation-group",
  "direct-users-and-organisation-level-and-project",
  "direct-project",
  "direct-by-group",
  "direct-by-user-group",
];

function Actions({ children, filters, setFilters, columns }) {
	const { t } = useTranslation();

	const baseConfig = useColumnFiltersConfig(columns);
	const { peopleCustomFields } = useCustomFields();

	const config = useMemo(() => {
		const filterConfig = [
			...baseConfig,
			{
				name: "payGroup",
				label: t("pay-group"),
				type: "resource-select",
				component: (
					<ResourceSelect
						labelPropName="description"
						resourcePath="/pay-group"
						renderLabel={renderSettingsResourceLabel}
						hasSearch
						mode="multiple"
						getSearchFilters={getSettingsResourceSearchFilters}
					/>
				),
			},
			{
				name: "employeeType",
				label: t("employee-type"),
				type: "resource-select",
				component: (
					<ResourceSelect
						labelPropName="description"
						renderLabel={renderSettingsResourceLabel}
						resourcePath="/employee-type"
						hasSearch
						mode="multiple"
						getSearchFilters={getSettingsResourceSearchFilters}
					/>
				),
			},
			{
				name: "project",
				label: t("project"),
				type: "resource-select",
				component: (
					<ResourceSelect
						labelPropName="description"
						renderLabel={renderSettingsResourceLabel}
						resourcePath="/project"
						hasSearch
						mode="multiple"
						getSearchFilters={getSettingsResourceSearchFilters}
					/>
				),
			},
			{
				name: "union",
				label: t("union"),
				type: "resource-select",
				component: (
					<ResourceSelect
						labelPropName="description"
						renderLabel={renderSettingsResourceLabel}
						resourcePath="/union"
						hasSearch
						mode="multiple"
						getSearchFilters={getSettingsResourceSearchFilters}
					/>
				),
			},
			{
				name: "companies",
				label: t("companies"),
				type: "resource-select",
				component: (
					<ResourceSelect
						labelPropName="description"
						resourcePath="/companies"
						hasSearch
						getSearchFilters={getCompaniesSearchFilters}
						mode="multiple"
						renderLabel={renderCompanyResourceLabel}
					/>
				),
			},
			{
				name: "calculationGroup",
				label: t("calculation-group"),
				type: "resource-select",
				component: (
					<ResourceSelect
						labelPropName="description"
						renderLabel={renderSettingsResourceLabel}
						resourcePath="/calculation-group"
						hasSearch
						mode="multiple"
						getSearchFilters={getSettingsResourceSearchFilters}
					/>
				),
			},
			{
				name: "roles",
				label: t("roles"),
				type: "resource-select",
				component: (
					<ResourceSelect
						labelPropName="description"
						renderLabel={renderSettingsResourceLabel}
						resourcePath="/user-roles"
						hasSearch
						mode="multiple"
						getSearchFilters={getSettingsResourceSearchFilters}
					/>
				),
			},
			{
				name: "holiday",
				label: t("holiday"),
				type: "resource-select",
				component: (
					<ResourceSelect
						labelPropName="description"
						renderLabel={renderSettingsResourceLabel}
						resourcePath="/holiday"
						hasSearch
						mode="multiple"
						getSearchFilters={getSettingsResourceSearchFilters}
					/>
				),
			},
			{
				name: "groupings",
				label: t("groupings"),
				type: "resource-select",
				component: (
					<ResourceSelect 
						labelPropName="description"
						renderLabel={renderSettingsResourceLabel}
						resourcePath="/grouping?excludedResources=pay-code"
						mode="multiple"
						getSearchFilters={getSettingsResourceSearchFilters}
					/>
				)
			},
			{
				name: "employmentType",
				label: t("employment-type"),
				type: "resource-select",
				component: (
					 <ResourceSelect
						resourcePath="/user-demographic/employment-type"
						mode="multiple"
						renderLabel={renderSettingsResourceLabel}
						getSearchFilters={getSettingsResourceSearchFilters}
						hasSearch
					/>
				)
			},
			{
        name: "salaryOrHourly",
        label: t("salary-or-hourly"),
        type: "select",
        component: (
					<Select>
						<Select.Option value="S">{t('salaried')}</Select.Option>
						<Select.Option value="H">{t('hourly')}</Select.Option>
          </Select>
				)
			},
			{
				name: "custom-field",
				label: t("custom-fields"),
				type: "resource-select",
				component: (
					 <ResourceSelect
						resourcePath="/custom-field/people"
						mode="multiple"
						renderLabel={renderSettingsResourceLabel}
						getSearchFilters={getSettingsResourceSearchFilters}
						hasSearch
					/>
				)
			},
			{
				name: "custom-fields",
				children: (peopleCustomFields || []).map((customField) => {
					return {
						name: customField.code,
						label: customField.code,
						type: customField.type === "select" ? "select" : "text",
						component: customField.type === "select" ? (
							<Select mode="multiple">
								{customField.options.map((option) => (
									<Select.Option key={option.value} value={option.value}>
										{option.label}
									</Select.Option>
								))}
							</Select>
						) : undefined,
					};
				}),
			},
			{
				name: "manageAbility",
				label: t("manage-ability"),
				type: "select",
				component: (
					<Select mode="multiple">
						{manageAbilityOptions.map((value) => (
							<Select.Option key={value} value={value}>
								{t(value)}
							</Select.Option>
						))}
					</Select>
				),
			},
		];

		return filterConfig;
	}, [t, baseConfig, peopleCustomFields]);

	return (
		<CardHeader className="border-top p-3">
			<Row className="d-flex align-items-center justify-content-between mb-3 mb-md-0">
				<Col md={12} lg={4}>{children}</Col>

				<Col md={12} lg={children ? 8 : 12}>
					<div className="d-flex align-items-start">
						<Filters
							config={config}
							filters={filters}
							setFilters={setFilters}
						/>

						<ColumnToggle columns={columns} />
					</div>
				</Col>
			</Row>
		</CardHeader>
	);
}

export default Actions;
